import { useMemo } from 'react';

import {
  FetchLifestylesSetGqlQuery,
  LifestylesSetUUID
} from '../../lifestylesSetsTypes';

import { FetchLifestylesSetCacheKey } from '../../lifestylesSetsTypes';

import { useShowQuery } from '../../../common/reactQuery/hooks/useShowQuery';

interface LifestylesSetResponse<ItemType> {
  lifestylesSet: ItemType | null;
}

interface LifestylesSetOptions<ItemType> {
  lifestylesSets?: ItemType[];
  cacheKey: FetchLifestylesSetCacheKey;
  query: FetchLifestylesSetGqlQuery;
  uuid: LifestylesSetUUID;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
}

interface UseLifestylesSetItem {
  uuid: LifestylesSetUUID;
}

const itemKey = 'lifestylesSet';

function useLifestylesSet<ItemType extends UseLifestylesSetItem>({
  lifestylesSets,
  cacheKey,
  query,
  uuid,
  options = {}
}: LifestylesSetOptions<ItemType>) {
  const placeholderData = useMemo<LifestylesSetResponse<ItemType>>(() => {
    const lifestylesSet = lifestylesSets?.find(
      (lifestylesSet) => lifestylesSet.uuid === uuid
    );

    return lifestylesSet ? { lifestylesSet } : null;
  }, [lifestylesSets, uuid]);

  const {
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemFetching,
    itemLoading,
    itemIsPlaceholderData,
    refetchItem,
    updateItemCache
  } = useShowQuery<LifestylesSetResponse<ItemType>, ItemType>({
    cacheKey,
    itemKey,
    options,
    placeholderData,
    query,
    uuid
  });

  return {
    lifestylesSet: item,
    lifestylesSetError: itemError,
    lifestylesSetErrorData: itemErrorData,
    lifestylesSetFetched: itemFetched,
    lifestylesSetFetching: itemFetching,
    lifestylesSetIsPlaceholderData: itemIsPlaceholderData,
    lifestylesSetLoading: itemLoading,
    lifestylesSetUpdateCache: updateItemCache,
    refetchLifestylesSet: refetchItem
  };
}

export default useLifestylesSet;
